import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { SessionProvider, useSession } from "@/contexts/SessionContext";
import { PulseProvider } from "@/contexts/PulseContext";
import NexaVaultDrawer from "@/components/smartpublish/NexaVaultDrawer";
import SubscriptionDrawer from "@/components/smartpublish/SubscriptionDrawer";
import LegacyClaimBanner from "@/components/smartpublish/LegacyClaimBanner";
import PulseFloatingPartner from "@/components/pulse/PulseFloatingPartner";
import PulseDrawer from "@/components/pulse/PulseDrawer";
import SmartPublishWorkspace from "@/pages/SmartPublishWorkspace";
import Dashboard from "@/pages/Dashboard";
import StrategyPage from "@/pages/StrategyPage";
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";

function AppRouter() {
    const location = useLocation();
    // Per Emergent Google Auth playbook: detect session_id in URL fragment
    // SYNCHRONOUSLY during render so the OAuth exchange runs before any
    // ProtectedRoute / /me check kicks off.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<ProtectedRoute><SmartPublishWorkspace /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/strategy" element={<ProtectedRoute><StrategyPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function ProtectedRoute({ children }) {
    const { auth, loading } = useSession();
    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#0B0F19] text-[#E8ECF5]">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#1F2A40] border-t-[#00F5D4]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                        loading workspace…
                    </span>
                </div>
            </div>
        );
    }
    if (!auth) return <LandingPage />;
    return (
        <>
            <LegacyClaimBanner />
            {children}
        </>
    );
}

function App() {
    return (
        <div className="App">
            <SessionProvider>
                <PulseProvider>
                    <BrowserRouter>
                        <AppRouter />
                        {/* Pulse floats on top of every authenticated route;
                            PulseFloatingPartner self-hides for anonymous. */}
                        <PulseFloatingPartner />
                        <PulseDrawer />
                    </BrowserRouter>
                    <NexaVaultDrawer />
                    <SubscriptionDrawer />
                    <Toaster
                        position="bottom-right"
                        theme="dark"
                        toastOptions={{
                            style: {
                                background: "#111726",
                                border: "1px solid #1F2A40",
                                color: "#E8ECF5",
                            },
                        }}
                    />
                </PulseProvider>
            </SessionProvider>
        </div>
    );
}

export default App;
